import { MAX_GPU_COUNT, checkComputeClassDeclaration, computeClassSchema, fixtureSizingSchema, scalingIntentSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/workers.schemas.ts
/**
* Zod schemas for the worker desired-state type referenced by `register-version`. R2 of
* `2026-10-14-app-pools-runtime-deletion` (R-081) removed the size-class schemas
* (`SizeClassMapSchema`, `WorkerResourcesSchema`).
*/
const WorkerRuntimeSchema = z.enum(["node", "python"]);
/**
* A worker's `subscribesTo` is an event TOPIC — one or more dot-delimited NATS
* subject tokens (e.g. `indexing`, `document.created`). §0.3 (relaxed from
* single-token): multi-segment `domain.event` topics are the platform
* convention, and the durability layer is dot-safe (dot-free durable name via
* `sanitizeForDurableName`; `zion.<app>.<topic>.>` filter keeps the dots). What
* is still rejected: wildcards (`*`/`>`), whitespace, the empty string, and a
* topic that is ALREADY a full subject (leading `zion.`) — the §0.3
* anti-pattern that would derive `zion.<app>.zion.docs.indexing.>`. Mirrors
* `assertValidTopic` in `@zion/sdk-contracts/event-subjects`.
*/
const TOPIC_TOKEN_PATTERN = /^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)*$/;
/** A topic must not already be a full subject (`zion.…`, case-insensitive). */
const RESERVED_TOPIC_PREFIX = /^zion\./i;
const subscribesToSchema = z.string().regex(TOPIC_TOKEN_PATTERN, "subscribesTo must be a NATS topic — one or more dot-delimited tokens (no wildcards or whitespace)").refine((t) => !RESERVED_TOPIC_PREFIX.test(t), "subscribesTo must not start with the reserved 'zion.' prefix (it is relative to the app scope, not a full subject)");
/** Mirror of `@zion/sdk-contracts` `FixtureResourceQuantities` for the typed worker wire (R-9 F-1).
*  `.strict()`; keys constrained to the same five the fixture schema admits, so the CP cannot forward
*  a key the DP will refuse.
*
*  ⚠️ Explicit keys here, `propertyNames` in the JSON schema — and they MUST agree. zod has no
*  `propertyNames`, so enumerating the five keys is the faithful translation.
*  `workers.schemas.spec.ts` pins the two lists equal by reading `fixture.schema.json` at test time,
*  because the failure mode of drift is one-directional and silent: the CP forwards a key the DP's Ajv
*  door will not accept, and the fixture stops deploying. */
const workerResourceQuantitiesSchema = z.object({
	cpu: z.string().min(1).max(32).optional(),
	memory: z.string().min(1).max(32).optional(),
	"ephemeral-storage": z.string().min(1).max(32).optional(),
	"hugepages-2Mi": z.string().min(1).max(32).optional(),
	"hugepages-1Gi": z.string().min(1).max(32).optional()
}).strict();
/** Mirror of `@zion/sdk-contracts` `FixtureComputeResources` for the typed worker wire (R-9 F-1).
*  `.strict()` at both levels. */
const workerComputeResourcesSchema = z.object({
	requests: workerResourceQuantitiesSchema.optional(),
	limits: workerResourceQuantitiesSchema.optional()
}).strict();
/**
* @deprecated `2026-09-04-app-pools`. Superseded by `AppPoolDesiredStateSchema` in
* `desired-state.ts`. See {@link WorkerDesiredState} on why the replacement is not a rename. This
* schema stays exported (R-081 item 2). Its one importer is `RegisterFixtureVersionDtoSchema` in
* `register-version.schemas.ts` of this package, and no source outside this package imports either
* schema. release-engine validates the `POST /v1/releases/versions` body with its own
* `workerDeclarationSchema` in `register-version.dto.ts`, so a change here changes no CP door. The
* desired-state wire has carried no `workers[]` since R2 of `2026-10-14-app-pools-runtime-deletion`
* (R-081 item 2), so no operator reads a worker either. The field notes below name the CP mappers
* (`buildWorkers`, `mapFixtureWorkersToDesiredState`) and the operator reads of the path before
* R2; the plan removed all of them. The tag means "build nothing new on this".
*/
const WorkerDesiredStateSchema = z.object({
	name: z.string().min(1).max(63),
	subscribesTo: subscribesToSchema,
	runtime: WorkerRuntimeSchema,
	entrypoint: z.string().min(1),
	sizeClass: z.string().min(1).optional(),
	machineType: z.string().min(1).max(40).optional(),
	resources: workerComputeResourcesSchema.optional(),
	maxReplicas: z.number().int().positive(),
	cooldownSeconds: z.number().int().nonnegative(),
	minReplicas: z.number().int().nonnegative().optional(),
	gracePeriodSeconds: z.number().int().positive().optional(),
	database: z.boolean(),
	blobStorage: z.boolean(),
	llm: z.boolean().optional(),
	coreApiAccess: z.boolean().optional(),
	orgCrypto: z.boolean().optional(),
	audit: z.boolean().optional(),
	nodeType: z.string().min(1).optional(),
	scalingIntent: scalingIntentSchema.optional(),
	class: computeClassSchema.optional(),
	gpuCount: z.number().int().min(1).max(MAX_GPU_COUNT).optional(),
	accelerator: z.string().min(1).max(40).regex(/^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/).optional(),
	sizing: fixtureSizingSchema.optional()
}).strict().refine((w) => w.minReplicas == null || w.minReplicas <= w.maxReplicas, "minReplicas must be ≤ maxReplicas").superRefine((w, ctx) => {
	for (const issue of checkComputeClassDeclaration(w, "", `worker '${w.name}'`)) ctx.addIssue({
		code: z.ZodIssueCode.custom,
		path: issue.path.split("/").filter((seg) => seg !== ""),
		message: `${issue.rule}: ${issue.message}`
	});
});
//#endregion
export { TOPIC_TOKEN_PATTERN, WorkerDesiredStateSchema, WorkerRuntimeSchema, subscribesToSchema, workerComputeResourcesSchema };

//# sourceMappingURL=workers.schemas.js.map